// Path of OUR Supabase Storage object inside `bucket`, or null for anything that must not be signed:
// blob:/data: previews, external favicons and Gravatar, other origins, other buckets, unsafe paths.
// Object keys are checked by the one validator shared with the edge functions (_shared/privacy/objectPath.ts).
import { isSafeObjectPath } from "@/components/gavion/domain/privacy";

export { isSafeObjectPath };
export const ATTACHMENTS = "attachments";
const STORAGE_RE =
  /^\/storage\/v1\/object\/(?:public|sign|authenticated)\/([^/]+)\/(.+)$/;
export type StorageFile =
  | { src?: string | null; path?: string | null }
  | null
  | undefined;

/** Origin of the configured Supabase project, or null when none is configured (FakeRest demo). */
export function originOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

export const SUPABASE_ORIGIN = originOf(import.meta.env.VITE_SUPABASE_URL);

/** True for any URL on our Supabase origin: such a URL is either signed or not rendered at all. */
export function isOwnOriginUrl(
  src: string | null | undefined,
  origin: string | null = SUPABASE_ORIGIN,
): boolean {
  return !!src && !!origin && src.startsWith(`${origin}/`);
}

export function storagePathOf(
  file: StorageFile,
  bucket: string = ATTACHMENTS,
  origin: string | null = SUPABASE_ORIGIN,
): string | null {
  const src = file?.src ?? "";
  if (!src) {
    const path = file?.path ?? "";
    return isSafeObjectPath(path) ? path : null;
  }
  // Exact origin prefix; read the raw path from the string, because URL parsing would
  // already have collapsed `..` segments.
  if (!origin || !src.startsWith(`${origin}/`)) return null;
  const rawPath = src.slice(origin.length).split(/[?#]/)[0];
  const m = STORAGE_RE.exec(rawPath);
  if (!m || m[1] !== bucket) return null;
  return isSafeObjectPath(m[2]) ? m[2] : null;
}
